import { h } from 'preact';
import { createMovie } from 'utils/data';
import { alert, Confirm } from 'utils/modal';
import slugify from 'utils/slugify';
import { getCurrentMovieUrl } from 'utils/studioConfig';

import Button from 'components/button/button';
import DateField from 'components/editor/settings/dateField';
import SimpleField from 'components/editor/settings/simpleField';

import s from 'components/movies/addMovieModal/addMovieModal.sss';

export default class AddMovieModal extends Confirm {
	s = s;

	get title() {
		return 'New movie';
	}

	get fields() {
		return {};
	}

	constructor(props) {
		super(props);
		this.state = {
			isCreating: false,
			showLongWait: false,
			data: {},
			errors: {},
		};

		// Field renderers
		this.renderTitleField = this.renderTitleField.bind(this);
		this.renderReleaseDateField = this.renderReleaseDateField.bind(this);
		this.renderSlugField = this.renderSlugField.bind(this);
		this.renderExternalUrlField = this.renderExternalUrlField.bind(this);

		// Event handlers
		this.createMovie = this.createMovie.bind(this);
		this.onFieldInput = this.onFieldInput.bind(this);
		this.onTitleInput = this.onTitleInput.bind(this);
		this.onSlugInput = this.onSlugInput.bind(this);
	}

	componentWillUnmount() {
		super.componentWillUnmount?.();
		clearTimeout(this.$longWaitTimer);
	}

	getValue(field) {
		return this.state.data[field];
	}

	setValue(field, value) {
		this.setState({ data: { ...this.state.data, [field]: value } });
		this.validateField(field);
	}

	getError(field) {
		return this.state.errors[field];
	}

	setError(field, error) {
		this.setState({ errors: { ...this.state.errors, [field]: error } });
	}

	getValidators(field) {
		return this.fields[field]?.validators || [];
	}

	slugMatchesTitle() {
		const { title, slug } = this.state.data;
		const titleAsSlug = slugify(title, { removeDiacritics: true });
		return slug === titleAsSlug;
	}

	onFieldInput(e) {
		this.setValue(e.target.name, e.target.value);
	}

	onTitleInput(e) {
		const title = e.target.value;
		const updateSlug = this.slugMatchesTitle();

		this.setValue('title', title);
		if (updateSlug) this.setValue('slug', slugify(title, { removeDiacritics: true }));
	}

	onSlugInput(e) {
		const { selectionStart, selectionEnd, value } = e.target;
	
		const newValue = slugify(value, { removeDiacritics: true, allowEdgeDashes: true });
		const lengthDiff = newValue.length - value.length;
	
		this.setValue('slug', newValue);
		e.target.setSelectionRange(selectionStart + lengthDiff, selectionEnd + lengthDiff); // Move cursor to correct position after transformation
	}

	validateField(field) {
		const value = this.getValue(field);
		const validators = this.getValidators(field);
		for (const validator of validators) {
			const error = validator(value);
			if (error) {
				this.setError(field, error);
				return false;
			}
		}
		this.setError(field, null);
		return true;
	}

	validateFields() {
		const fields = Object.keys(this.fields);
		const validationResults = fields.map(field => this.validateField(field));
		return validationResults.every(result => result);
	}

	getNewMovieOptions() {
		return this.state.data;
	}

	async createMovie() {
		const isValid = this.validateFields();
		if (!isValid) return;

		this.setState({ isCreating: true });
		this.$longWaitTimer = setTimeout(() => this.setState({ showLongWait: true }), 60000);
		try {
			const options = this.getNewMovieOptions();
			const newMovie = await createMovie(options);

			if (!newMovie?.id) {
				throw new Error('Failed to create new movie');
			}

			// Close the modal and return the new movie data to the caller
			this.base.close(JSON.stringify(newMovie));
		} catch (err) {
			console.error('Error creating movie', err);
			alert('Sorry, an error occurred while creating a new movie', { title: 'Error' });
		} finally {
			clearTimeout(this.$longWaitTimer);
			this.setState({ isCreating: false, showLongWait: false });
		}
	}

	renderTitleField(opts = {}) {
		return (
			<SimpleField
				key="title"
				name="title"
				label="Movie title"
				value={this.getValue('title')}
				onInput={this.onTitleInput}
				error={this.getError('title')}
				{...opts}
			/>
		);
	}

	renderReleaseDateField(opts = {}) {
		return (
			<DateField
				key="release"
				name="release"
				label="Release date"
				help="The date the movie releases"
				value={this.getValue('release')}
				onInput={this.onFieldInput}
				error={this.getError('release')}
				allowCustomPrecision
				{...opts}
			/>
		);
	}

	renderSlugField(opts = {}) {
		return (
			<SimpleField
				key="slug"
				name="slug"
				label="Movie URL"
				preText={getCurrentMovieUrl()}
				value={this.getValue('slug')}
				onInput={this.onSlugInput}
				error={this.getError('slug')}
				{...opts}
			/>
		);
	}

	renderExternalUrlField(opts = {}) {
		return (
			<SimpleField
				type="url"
				key="externalUrl"
				name="url"
				label="Use external URL"
				help="Sets a redirect to the specified URL"
				placeholder="https://www.example.com/"
				value={this.getValue('url')}
				onInput={this.onFieldInput}
				error={this.getError('url')}
				toggleable
				{...opts}
			/>
		);
	}

	renderContent() {
		return (
			<div class={this.s.content}>
				{Object.values(this.fields).map(field => field.render())}
			</div>
		);
	}

	renderActionButton() {
		const { errors, isCreating } = this.state;
		const hasErrors = Object.values(errors).some(error => !!error);
		const disabled = isCreating || hasErrors;

		return (
			<Button
				type="button"
				primary
				iconAlt="chevron-right"
				disabled={disabled}
				loading={isCreating}
				onClick={this.createMovie}
			>
				{isCreating ? 'Creating movie' : 'Create movie'}
			</Button>
		);
	}

	renderActions() {
		const base = super.renderActions();
		if (base && this.state.showLongWait) {
			base.children.unshift(<span class={this.s.loadingMessage}>We're still working on it...</span>);
		}
		return base;
	}
}
